import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Key, Loader as Loader2, Lock, LogOut, Mail, TriangleAlert as AlertTriangle,
  Search, ShieldCheck, Star, UserPlus, RefreshCw, X, ArrowUpDown, ChevronDown, Filter
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { callSiteApi } from '../utils';
import { CreateUserModal } from './CreateUserModal';
import type { Site, WPUser } from '../types';

interface WpUsersTabProps {
  site: Site;
  onUpdateSite?: (updates: Partial<Site>) => void;
}

type SortOption = 'a-z' | 'z-a' | 'newest' | 'oldest';

const roleFilters = [
  { label: 'All Users', value: '' },
  { label: 'Administrators', value: 'administrator' },
  { label: 'Editors', value: 'editor' },
  { label: 'Authors', value: 'author' },
  { label: 'Contributors', value: 'contributor' },
  { label: 'Subscribers', value: 'subscriber' },
];

async function loadCachedUsers(siteId: string): Promise<WPUser[]> {
  const { data, error } = await supabase
    .from('site_wp_users')
    .select('*')
    .eq('site_id', siteId)
    .order('display_name');
  if (error || !data) return [];
  return data.map((row: any) => ({
    id: row.wp_user_id,
    username: row.username,
    display_name: row.display_name,
    email: row.email,
    roles: Array.isArray(row.roles) ? row.roles : [],
    avatar_url: row.avatar_url,
    registered: row.registered,
  }));
}

async function syncUsersToCache(siteId: string, users: WPUser[]) {
  await supabase.from('site_wp_users').delete().eq('site_id', siteId);

  if (users.length === 0) return;

  const rows = users.map((u) => ({
    site_id: siteId,
    wp_user_id: u.id,
    username: u.username,
    display_name: u.display_name,
    email: u.email,
    roles: u.roles,
    avatar_url: u.avatar_url,
    registered: u.registered || '',
    updated_at: new Date().toISOString(),
  }));

  await supabase.from('site_wp_users').insert(rows);
}

export const WpUsersTab: React.FC<WpUsersTabProps> = ({ site, onUpdateSite }) => {
  const { toast } = useToast();
  const [users, setUsers] = useState<WPUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('a-z');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isResetting, setIsResetting] = useState<number | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const loadFromCache = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const cached = await loadCachedUsers(site.id);
      if (cached.length > 0) {
        setUsers(cached);
        setLoading(false);
      } else {
        await refreshFromApi();
      }
    } catch {
      await refreshFromApi();
    }
  }, [site.id]);

  const refreshFromApi = async () => {
    try {
      setError(null);
      const res = await callSiteApi(site.url, site.publicKey, site.secretKey, 'users');
      const fetched: WPUser[] = res.users ?? [];
      setUsers(fetched);
      await syncUsersToCache(site.id, fetched);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  const handleReload = async () => {
    setReloading(true);
    await refreshFromApi();
    if (!error) {
      toast({ title: 'Users refreshed', description: 'User list has been updated from the website.' });
    }
  };

  useEffect(() => {
    loadFromCache();
  }, [site.id]);

  const handleMagicLogin = async (userId: number) => {
    try {
      const body = JSON.stringify({ user_id: userId });
      const res = await callSiteApi(site.url, site.publicKey, site.secretKey, 'magic-login', 'POST', body);
      if (res.success && res.login_url) {
        window.open(res.login_url, '_blank');
      } else {
        toast({ title: 'Magic Login Failed', description: res.message || 'Failed to generate magic login link.', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: 'Magic Login Error', description: err.message || 'Network error during magic login.', variant: 'destructive' });
    }
  };

  const handleSetDefaultUser = async (user: WPUser) => {
    try {
      const { error } = await supabase
        .from('workspace_sites')
        .update({ default_wp_user_id: user.id, default_wp_user_name: user.display_name })
        .eq('id', site.id);
      if (error) throw error;
      if (onUpdateSite) {
        onUpdateSite({ defaultWpUserId: user.id, defaultWpUserName: user.display_name });
      }
    } catch (err: any) {
      console.error('Failed to set default user:', err);
    }
  };

  const handleResetPassword = async (userId: number) => {
    setIsResetting(userId);
    try {
      await callSiteApi(site.url, site.publicKey, site.secretKey, `users/${userId}`, 'POST');
      toast({ title: 'Reset Email Sent', description: 'A password reset email has been sent to the user.' });
    } catch (e: any) {
      toast({ title: 'Reset Failed', description: e.message || 'Could not send reset email.', variant: 'destructive' });
    } finally {
      setIsResetting(null);
    }
  };

  const filteredUsers = users
    .filter((u) => {
      if (roleFilter && !u.roles.includes(roleFilter)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          u.display_name.toLowerCase().includes(q) ||
          u.username.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'a-z': return a.display_name.localeCompare(b.display_name);
        case 'z-a': return b.display_name.localeCompare(a.display_name);
        case 'newest': return new Date(b.registered || 0).getTime() - new Date(a.registered || 0).getTime();
        case 'oldest': return new Date(a.registered || 0).getTime() - new Date(b.registered || 0).getTime();
        default: return 0;
      }
    });

  const sortOptions = [
    { value: 'a-z', label: 'A-Z' },
    { value: 'z-a', label: 'Z-A' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
  ];

  const isSortActive = sortBy !== 'a-z';
  const isFilterActive = roleFilter !== '';

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-foreground">Website Users</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex items-center h-10">
            <AnimatePresence initial={false}>
              {searchOpen ? (
                <motion.div
                  key="search-open"
                  className="relative flex items-center h-10"
                  initial={{ width: 40, opacity: 0.6 }}
                  animate={{ width: 256, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search users..."
                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                    className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                  <button
                    onMouseDown={(e) => { e.preventDefault(); setSearchOpen(false); setSearchQuery(''); }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="search-icon"
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Search className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <div className="relative h-10">
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className={"flex items-center space-x-2 px-4 h-full rounded-lg border transition-colors " + (isFilterActive
                ? 'bg-background shadow-sm text-foreground border-transparent'
                : 'bg-card border-border text-foreground hover:bg-muted'
              )}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span className="text-sm font-medium whitespace-nowrap">
                {roleFilter ? roleFilters.find(r => r.value === roleFilter)?.label : 'All Users'}
              </span>
              <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (showFilterMenu ? 'rotate-180' : '')} />
            </button>
            <AnimatePresence>
              {showFilterMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowFilterMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                  >
                    {roleFilters.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => { setRoleFilter(option.value); setShowFilterMenu(false); }}
                        className={"w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg " +
                          (roleFilter === option.value
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-foreground hover:bg-accent'
                          )}
                      >
                        <span>{option.label}</span>
                        {roleFilter === option.value && <div className="w-2 h-2 bg-current rounded-full" />}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <div className="relative h-10">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className={"p-2.5 h-full rounded-lg border transition-colors " + (isSortActive
                ? 'bg-background shadow-sm text-foreground border-transparent'
                : 'bg-card border-border text-foreground hover:bg-muted'
              )}
              title="Sort users"
            >
              <Filter className="w-4 h-4" />
            </button>
            <AnimatePresence>
              {showSortMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowSortMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                  >
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => { setSortBy(option.value as SortOption); setShowSortMenu(false); }}
                        className={"w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg " +
                          (sortBy === option.value
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-foreground hover:bg-accent'
                          )}
                      >
                        <span>{option.label}</span>
                        {sortBy === option.value && <div className="w-2 h-2 bg-current rounded-full" />}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={handleReload}
            disabled={reloading}
            className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            title="Refresh users from website"
          >
            <RefreshCw className={"w-4 h-4 " + (reloading ? 'animate-spin' : '')} />
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-6 h-10 bg-workspace-accent text-white rounded-md transition-colors hover:bg-workspace-accent-hover whitespace-nowrap"
          >
            <UserPlus className="w-5 h-5" />
            <span>New User</span>
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="divide-y divide-border">
          {loading ? (
            <div className="flex flex-col gap-3 p-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-muted/30 animate-pulse rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-foreground">Failed to Load Users</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">{error}</p>
              <button onClick={handleReload} className="mt-6 px-6 py-2 bg-muted hover:bg-muted-foreground/10 rounded-xl text-sm font-bold transition-colors">
                Try Again
              </button>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">No users found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or filters.</p>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user.id} className="group hover:bg-muted/10 transition-colors p-4 md:p-5 flex items-center justify-between">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="relative shrink-0">
                    <img
                      src={user.avatar_url}
                      alt=""
                      className="w-11 h-11 rounded-full bg-muted border border-border/10"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user.display_name) + '&background=random&color=fff&size=64';
                      }}
                    />
                    {user.roles.includes('administrator') && (
                      <div className="absolute -bottom-1 -right-1 bg-amber-500 rounded-full p-1 border-2 border-card shadow-sm" title="Administrator">
                        <ShieldCheck className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-foreground truncate">{user.display_name}</span>
                      {site.defaultWpUserId === user.id && (
                        <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5 truncate">
                        <Lock className="w-3 h-3" /> @{user.username}
                      </span>
                      <span className="flex items-center gap-1.5 truncate">
                        <Mail className="w-3 h-3" /> {user.email}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <div className="hidden lg:flex flex-col items-end mr-4">
                    <div className="flex gap-1.5">
                      {user.roles.map(role => (
                        <span key={role} className={"text-[10px] font-bold px-2 py-0.5 rounded-full capitalize " + (
                          role === 'administrator' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                          role === 'editor' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                          'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                        )}>
                          {role}
                        </span>
                      ))}
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-1">Joined {new Date(user.registered).toLocaleDateString()}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button onClick={() => handleMagicLogin(user.id)} title="Magic Login" className="p-2.5 rounded-lg text-muted-foreground hover:text-workspace-accent hover:bg-workspace-accent/10 transition-all active:scale-90">
                      <LogOut className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetPassword(user.id)}
                      disabled={isResetting === user.id}
                      title="Send Reset Password Email"
                      className="p-2.5 rounded-lg text-muted-foreground hover:text-blue-500 hover:bg-blue-500/10 transition-all active:scale-90 disabled:opacity-50"
                    >
                      {isResetting === user.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleSetDefaultUser(user)}
                      title={site.defaultWpUserId === user.id ? 'Default User' : 'Set as Default'}
                      className={"p-2.5 rounded-lg transition-all active:scale-90 " + (site.defaultWpUserId === user.id ? 'text-yellow-500 bg-yellow-500/10' : 'text-muted-foreground hover:text-yellow-500 hover:bg-yellow-500/10')}
                    >
                      <Star className={"w-4 h-4 " + (site.defaultWpUserId === user.id ? 'fill-current' : '')} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <AnimatePresence>
        {showCreateModal && (
          <CreateUserModal
            site={site}
            onClose={() => setShowCreateModal(false)}
            onCreated={() => { setShowCreateModal(false); handleReload(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
